<template>
  <div>
    <!-- Full screen overlay when consent is not given -->
    <div 
      v-if="!isConsentGiven && isVisible"
      class="fixed inset-0 bg-black bg-opacity-60 z-[99998] flex items-center justify-center"
    >
      <!-- Centered consent dialog -->
      <div
        class="z-[99999] bg-gray-900 border border-gray-700 shadow-2xl rounded-xl w-full max-w-sm sm:max-w-md md:max-w-lg mx-4 p-5 text-white"
      >
        <div class="flex flex-col">
          <!-- Banner Content -->
          <div class="flex-1 mb-6">
            <div class="text-xl font-semibold mb-4 text-white text-center">
              Používáme soubory cookies
            </div>
            <p class="text-gray-300 text-sm leading-relaxed">
              Naším přáním je, abyste využívali naši službu na maximum, a k tomu
              potřebujeme cookies.
            </p>
            <p class="text-gray-300 text-sm leading-relaxed mt-3">
              Cookies jsou nezbytné k správnému fungování webu, vyhledávání,
              přihlašování, personifikaci, vytváření analýz a statistik a celé
              řadě dalších věcí.
            </p>
            <p class="text-gray-300 text-sm leading-relaxed mt-3">
              V souladu s požadavky legislativy vyberte prosím jednu z nabízených
              možností.
            </p>
          </div>

          <!-- Buttons -->
          <div class="flex flex-col gap-3">
            <button
              @click="acceptAll"
              class="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Souhlasím, chci pokračovat!
            </button>
            <div class="flex flex-col sm:flex-row gap-3">
              <button
                @click="showPreferencesModal"
                class="flex-1 px-4 py-3 border border-gray-600 rounded-lg text-gray-200 hover:bg-gray-800 transition-colors duration-200 font-medium"
              >
                Přizpůsobit nastavení
              </button>
              <button
                @click="rejectAll"
                class="flex-1 px-4 py-3 border border-gray-600 rounded-lg text-gray-200 hover:bg-gray-800 transition-colors duration-200 font-medium"
              >
                Nechci, nesouhlasím
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Use the separate preferences modal component -->
    <cookie-preferences-modal
      :visible="isPreferencesVisible"
      :title="headerTitle"
      :description="headerDescription"
      :preferences="preferences"
      v-model="selectedPreferences"
      @close="closePreferencesModal"
      @save="savePreferences"
      @accept-all="acceptAll"
    />
  </div>
</template>

<script>
import CookiePreferencesModal from "./CookiePreferencesModal.vue";
import { initializeTracking } from "@/utils/trackingManager";
import router from "@/router";

export default {
  name: "CookieConsent",
  components: {
    CookiePreferencesModal,
  },
  props: {
    headerTitle: {
      type: String,
      default: "Cookie settings",
    },
    headerDescription: {
      type: String,
      default:
        "We use cookies and similar technologies to help personalize content and offer a better experience. You can opt to customize them by clicking the preferences button.",
    },
    preferencesLabel: {
      type: String,
      default: "Preferences",
    },
    acceptAllLabel: {
      type: String,
      default: "Accept All",
    },
    preferences: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isVisible: true,
      isPreferencesVisible: false,
      isConsentGiven: false,
      selectedPreferences: [],
    };
  },
  mounted() {
    // Check if consent already exists in localStorage
    const cookieConsent = localStorage.getItem("cookie-comply");
    if (cookieConsent) {
      this.isConsentGiven = true;

      // If it's an array (saved preferences), parse it
      if (cookieConsent !== "all") {
        try {
          this.selectedPreferences = JSON.parse(cookieConsent);
          
          // Determine which categories are enabled
          const hasStatistics = this.selectedPreferences.includes('statistics');
          const hasMarketing = this.selectedPreferences.includes('marketing');
          const hasPreferences = this.selectedPreferences.includes('preferences');
          
          // Push consent to dataLayer directly (for GTM container) with full Consent Mode parameters
          if (window.dataLayer) {
            window.dataLayer.push({
              'event': 'consent_update',
              // Google Consent Mode v2 parameters
              'analytics_storage': hasStatistics ? 'granted' : 'denied',
              'ad_storage': hasMarketing ? 'granted' : 'denied',
              'ad_user_data': hasMarketing ? 'granted' : 'denied',
              'ad_personalization': hasMarketing ? 'granted' : 'denied',
              'functionality_storage': 'granted', // For essential cookies, always granted
              'security_storage': 'granted', // For security purposes, always granted
              
              // Keep original parameters for backward compatibility
              'cookie_consent_statistics': hasStatistics,
              'cookie_consent_marketing': hasMarketing,
              'cookie_consent_preferences': hasPreferences,
              'consent_statistics': hasStatistics,
              'consent_marketing': hasMarketing,
              'consent_preferences': hasPreferences
            });
          }
          
          // Initialize tracking with saved preferences and pass router for GTM
          initializeTracking(this.selectedPreferences, router);
        } catch (e) {
          console.error("Error parsing cookie preferences:", e);
        }
      } else {
        // If 'all', select all preferences
        this.selectedPreferences = this.getAllValues();
        
        // Push consent to dataLayer directly (for GTM container) with full Consent Mode parameters
        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'consent_update',
            // Google Consent Mode v2 parameters
            'analytics_storage': 'granted',
            'ad_storage': 'granted',
            'ad_user_data': 'granted',
            'ad_personalization': 'granted',
            'functionality_storage': 'granted', // For essential cookies, always granted
            'security_storage': 'granted', // For security purposes, always granted
            
            // Keep original parameters for backward compatibility
            'cookie_consent_statistics': true,
            'cookie_consent_marketing': true,
            'cookie_consent_preferences': true,
            'consent_statistics': true,
            'consent_marketing': true,
            'consent_preferences': true
          });
        }
        
        // Initialize tracking with all preferences and pass router for GTM
        initializeTracking(this.selectedPreferences, router);
      }
    } else {
      // Initialize with required or enabled items
      this.initializePreferences();
      // Do not initialize tracking yet as user hasn't made a choice
    }
  },
  methods: {
    initializePreferences() {
      // Select all required items and items marked as isEnable
      this.selectedPreferences = [];

      this.preferences.forEach((category) => {
        category.items.forEach((item) => {
          if (item.isRequired || item.isEnable) {
            this.selectedPreferences.push(item.value);
          }
        });
      });
    },
    getAllValues() {
      const allValues = [];
      this.preferences.forEach((category) => {
        category.items.forEach((item) => {
          allValues.push(item.value);
        });
      });
      return allValues;
    },
    showPreferencesModal() {
      console.log("Show preferences clicked");
      this.isPreferencesVisible = true;
    },
    closePreferencesModal() {
      this.isPreferencesVisible = false;
    },
    acceptAll() {
      console.log("Accept all clicked");
      // Select all preferences
      this.selectedPreferences = this.getAllValues();

      // Save consent
      localStorage.setItem("cookie-comply", "all");
      this.isConsentGiven = true;
      this.isPreferencesVisible = false;

      // Push consent to dataLayer directly (for GTM container) with full Consent Mode parameters
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'consent_update',
          // Google Consent Mode v2 parameters
          'analytics_storage': 'granted',
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted', 
          'functionality_storage': 'granted', // For essential cookies, always granted
          'security_storage': 'granted', // For security purposes, always granted
          
          // Keep original parameters for backward compatibility
          'cookie_consent_statistics': true,
          'cookie_consent_marketing': true,
          'cookie_consent_preferences': true,
          'consent_statistics': true,
          'consent_marketing': true,
          'consent_preferences': true
        });
      }

      // Initialize tracking with all preferences and pass router for GTM
      initializeTracking(this.selectedPreferences, router);

      this.$emit("on-accept-all-cookies");
    },

    rejectAll() {
      console.log("Reject all clicked");
      // Only select required cookies
      this.selectedPreferences = [];

      // Initialize with only required items
      this.preferences.forEach((category) => {
        category.items.forEach((item) => {
          if (item.isRequired) {
            this.selectedPreferences.push(item.value);
          }
        });
      });

      // Save consent (only required cookies)
      localStorage.setItem(
        "cookie-comply",
        JSON.stringify(this.selectedPreferences)
      );
      this.isConsentGiven = true;

      // Push consent to dataLayer directly (for GTM container) with full Consent Mode parameters
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'consent_update',
          // Google Consent Mode v2 parameters
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'functionality_storage': 'granted', // For essential cookies, always granted
          'security_storage': 'granted', // For security purposes, always granted
          
          // Keep original parameters for backward compatibility
          'cookie_consent_statistics': false,
          'cookie_consent_marketing': false,
          'cookie_consent_preferences': false,
          'consent_statistics': false,
          'consent_marketing': false,
          'consent_preferences': false
        });
      }

      // Initialize tracking with only required preferences and pass router for GTM
      initializeTracking(this.selectedPreferences, router);

      this.$emit("on-save-cookie-preferences", this.selectedPreferences);
    },
    savePreferences(preferences) {
      console.log("Save preferences", preferences);
      // Save preferences to localStorage
      localStorage.setItem("cookie-comply", JSON.stringify(preferences));
      this.isConsentGiven = true;
      this.isPreferencesVisible = false;

      // Determine which categories are enabled
      const hasStatistics = preferences.includes('statistics');
      const hasMarketing = preferences.includes('marketing');
      const hasPreferences = preferences.includes('preferences');

      // Push consent to dataLayer directly (for GTM container) with full Consent Mode parameters
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'consent_update',
          // Google Consent Mode v2 parameters
          'analytics_storage': hasStatistics ? 'granted' : 'denied',
          'ad_storage': hasMarketing ? 'granted' : 'denied',
          'ad_user_data': hasMarketing ? 'granted' : 'denied',
          'ad_personalization': hasMarketing ? 'granted' : 'denied',
          'functionality_storage': 'granted', // For essential cookies, always granted
          'security_storage': 'granted', // For security purposes, always granted
          
          // Keep original parameters for backward compatibility
          'cookie_consent_statistics': hasStatistics,
          'cookie_consent_marketing': hasMarketing,
          'cookie_consent_preferences': hasPreferences,
          'consent_statistics': hasStatistics,
          'consent_marketing': hasMarketing,
          'consent_preferences': hasPreferences
        });
      }

      // Initialize tracking with user-selected preferences and pass router for GTM
      initializeTracking(preferences, router);

      this.$emit("on-save-cookie-preferences", preferences);
    },
  },
};
</script>